import { canCloseTimesheet, canCreateInvoice, canReopen, dbErrorMessage } from "@/lib/closeRules";
import { Archive, ArchiveRestore, ExternalLink, Receipt, RefreshCw } from "lucide-react";
import { parseDecimal } from "@/lib/parseDecimal";
import { useEffect, useMemo, useRef, useState } from "react";
import { getAutosaveBlock } from "@/lib/autosave";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Ban, Check, Copy, FileDown, Loader2, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { generateLnwPdf } from "@/lib/lnwPdf";
import { HourTotals, fmtEur, loadOrderHourTotals } from "@/lib/lnwHours";

interface OrderOption {
  id: string;
  name: string;
  order_number: string | null;
  hourly_rate: number | null;
  budget_hours: number | null;
  external_hours?: number | null;
  project_id: string;
  status: string;
  closed_at?: string | null;
  contact: { name: string; email: string | null } | null;
  projects: { id: string; name: string; customers: { id: string; company: string; email: string | null } | null } | null;
}

interface ItemRow {
  key: string;
  work_date: string;
  hours: string;
  description: string;
}

interface AiResult {
  items: Array<{ work_date: string; hours: number; description: string }>;
  warnings: string[];
}

type Status = "draft" | "sent" | "approved" | "rejected";

const statusLabel: Record<Status, string> = {
  draft: "Entwurf",
  sent: "Versendet",
  approved: "Genehmigt",
  rejected: "Abgelehnt",
};

const defaultLnwNumber = () => {
  const d = new Date();
  return `${String(Date.now()).slice(0, 10)}_${String(d.getFullYear()).slice(2)}_${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtHours = (h: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(h);

/** Sort rows by content so the change snapshot ignores row order (positions are stored date-sorted anyway). */
const sortSnapshotItems = <T extends { work_date: string; hours: string; description: string }>(
  rows: T[],
): T[] =>
  [...rows].sort((a, b) =>
    `${a.work_date}|${a.hours}|${a.description}`.localeCompare(`${b.work_date}|${b.hours}|${b.description}`),
  );

const newItem = (): ItemRow => ({
  key: crypto.randomUUID(),
  work_date: format(new Date(), "yyyy-MM-dd"),
  hours: "",
  description: "",
});

const AdminTimesheetEdit = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "neu";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get("order") ?? "";

  const [orders, setOrders] = useState<OrderOption[]>([]);
  const [orderId, setOrderId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [orderTotals, setOrderTotals] = useState<Record<string, HourTotals>>({});
  const [lnwNumber, setLnwNumber] = useState(defaultLnwNumber());
  const [issueDate, setIssueDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemRow[]>([newItem()]);
  const [status, setStatus] = useState<Status>("draft");
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [approvedAt, setApprovedAt] = useState<string | null>(null);
  const [approvedByName, setApprovedByName] = useState<string | null>(null);
  const [rejectionComment, setRejectionComment] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [approvalLink, setApprovalLink] = useState("");
  const [approvalToken, setApprovalToken] = useState("");
  const [approvalTimesheetId, setApprovalTimesheetId] = useState("");
  const [tokenExpiresAt, setTokenExpiresAt] = useState<string | null>(null);
  const hasActiveLink = Boolean(tokenExpiresAt && new Date(tokenExpiresAt) > new Date());
  const [activeLink, setActiveLink] = useState("");
  const [activeLinkUnavailable, setActiveLinkUnavailable] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [activityNotes, setActivityNotes] = useState("");
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [savedChangeReason, setSavedChangeReason] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autosaveError, setAutosaveError] = useState(false);
  const savingRef = useRef<Promise<string | null> | null>(null);
  const savedIdRef = useRef<string | null>(null);
  const [closedAt, setClosedAt] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [invoice, setInvoice] = useState<{ id: string | null; number: string | null; invoicedAt: string | null }>({ id: null, number: null, invoicedAt: null });
  const [invoiceText, setInvoiceText] = useState("");
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [{ data: o }, totals] = await Promise.all([
        supabase
          .from("orders")
          .select("id, name, order_number, hourly_rate, budget_hours, external_hours, project_id, status, closed_at, contact:customer_contacts(name, email), projects(id, name, customers(id, company, email))")
          .order("name"),
        loadOrderHourTotals(),
      ]);
      const loadedOrders = (o ?? []) as unknown as OrderOption[];
      setOrders(loadedOrders);
      setOrderTotals(totals);

      if (isNew && presetOrderId) {
        const preset = loadedOrders.find((ord) => ord.id === presetOrderId);
        if (preset) {
          setOrderId(preset.id);
          setProjectId(preset.project_id);
          setCustomerId(preset.projects?.customers?.id ?? "");
        }
      }

      if (!isNew && id) {
        const [{ data: ts, error }, { data: its }] = await Promise.all([
          supabase.from("timesheets").select("*").eq("id", id).single(),
          supabase.from("timesheet_items").select("*").eq("timesheet_id", id).order("position"),
        ]);
        if (error || !ts) {
          toast.error("Leistungsnachweis nicht gefunden");
          navigate("/admin");
          return;
        }
        setOrderId(ts.order_id);
        const existingOrder = loadedOrders.find((ord) => ord.id === ts.order_id);
        setProjectId(existingOrder?.project_id ?? "");
        setCustomerId(existingOrder?.projects?.customers?.id ?? "");
        setLnwNumber(ts.lnw_number);
        setIssueDate(ts.issue_date);
        setNotes(ts.notes ?? "");
        setStatus(ts.status as Status);
        setClosedAt(ts.closed_at);
        setInvoice({ id: ts.lexware_invoice_id, number: ts.invoice_number, invoicedAt: ts.invoiced_at });
        setSentToEmail(ts.sent_to_email);
        setTokenExpiresAt(ts.token_hash && !ts.token_revoked_at ? ts.token_expires_at : null);
        setSavedChangeReason(ts.change_reason);
        setApprovedAt(ts.approved_at);
        setApprovedByName(ts.approved_by_name);
        setRejectionComment(ts.rejection_comment);
        const loadedItems = (its ?? []).map((i) => ({
          key: i.id,
          work_date: i.work_date,
          hours: String(i.hours).replace(".", ","),
          description: i.description,
        }));
        setItems(loadedItems.length > 0 ? loadedItems : [newItem()]);
        setInitialSnapshot(
          JSON.stringify({
            lnwNumber: ts.lnw_number,
            issueDate: ts.issue_date,
            notes: ts.notes ?? "",
            items: sortSnapshotItems(loadedItems).map((i) => [i.work_date, i.hours, i.description]),
          }),
        );
      }
      setLoading(false);
    };
    load();
  }, [id, isNew, navigate, presetOrderId]);

  const order = orders.find((o) => o.id === orderId) ?? null;
  const customer = order?.projects?.customers ?? null;
  const recipientEmail = order?.contact?.email ?? customer?.email ?? null;
  const locked = status === "approved" || !!closedAt;
  const closed = !!closedAt;

  const fnError = async (error: unknown) => {
    if (error instanceof FunctionsHttpError) {
      const body = await error.context.json().catch(() => null);
      return body?.error ? `${body.error}${body.details ? `: ${String(body.details).slice(0, 200)}` : ""}` : "Lexware-Aufruf fehlgeschlagen";
    }
    return "Lexware-Aufruf fehlgeschlagen";
  };

  const createInvoice = async () => {
    if (!id) return;
    const check = canCreateInvoice({ status, lexware_invoice_id: invoice.id });
    if (!check.ok) return toast.error(check.reason);
    setInvoiceBusy(true);
    const { data, error } = await supabase.functions.invoke("lexware-create-invoice", { body: { timesheetId: id, text: invoiceText } });
    setInvoiceBusy(false);
    if (error) return toast.error(await fnError(error));
    setInvoice({ id: data.invoiceId, number: null, invoicedAt: null });
    toast.success("Rechnungsentwurf in Lexware angelegt");
  };

  const refreshInvoice = async (silent: boolean) => {
    if (!id) return;
    setInvoiceBusy(true);
    const { data, error } = await supabase.functions.invoke("lexware-invoice-status", { body: { timesheetId: id } });
    setInvoiceBusy(false);
    if (error) { if (!silent) toast.error(await fnError(error)); return; }
    if (data.deleted) {
      setInvoice({ id: null, number: null, invoicedAt: null });
      if (!silent) toast.info("Entwurf wurde in Lexware gelöscht, ein neuer kann angelegt werden");
      return;
    }
    setInvoice((prev) => ({ id: prev.id, number: data.invoice_number ?? null, invoicedAt: data.invoiced_at ?? null }));
    if (!silent) toast.success(data.invoiced_at ? "Rechnung ist fertiggestellt" : "Rechnung ist noch ein Entwurf");
  };

  const markInvoiced = async () => {
    if (!id) return;
    const now = new Date().toISOString();
    const { error } = await supabase.from("timesheets").update({ invoiced_at: now, invoice_status: "manual" }).eq("id", id);
    if (error) return toast.error(dbErrorMessage(error, "Speichern fehlgeschlagen"));
    setInvoice((prev) => ({ ...prev, invoicedAt: now }));
    toast.success("Als abgerechnet markiert");
  };

  useEffect(() => {
    if (!invoice.id || closedAt) return;
    let busy = false;
    const sync = async () => {
      if (busy || document.hidden) return;
      busy = true;
      try { await refreshInvoice(true); } finally { busy = false; }
    };
    void sync();
    window.addEventListener("focus", sync);
    const timer = window.setInterval(sync, 120_000);
    return () => { window.removeEventListener("focus", sync); window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, invoice.id, closedAt]);

  useEffect(() => {
    if (!invoiceText && lnwNumber) setInvoiceText(`Beratungsleistung gemäß Leistungsnachweis ${lnwNumber}${order?.order_number ? `, Auftrag ${order.order_number}` : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lnwNumber, order?.order_number]);

  const customerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orders) {
      const c = o.projects?.customers;
      if (c) map.set(c.id, c.company);
    }
    return [...map.entries()].map(([id, company]) => ({ id, company })).sort((a, b) => a.company.localeCompare(b.company));
  }, [orders]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orders) {
      const p = o.projects;
      if (p && (!customerId || p.customers?.id === customerId)) map.set(p.id, p.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [orders, customerId]);

  const orderOptions = useMemo(
    () => orders.filter((o) => (!o.closed_at || o.id === orderId) && (!projectId || o.project_id === projectId) && (!customerId || o.projects?.customers?.id === customerId)),
    [orders, customerId, projectId, orderId],
  );

  const handleCustomerChange = (value: string) => {
    setCustomerId(value);
    setProjectId("");
    setOrderId("");
  };
  const handleProjectChange = (value: string) => {
    setProjectId(value);
    setOrderId("");
  };

  const isRowEmpty = (i: ItemRow) => !i.hours.trim() && !i.description.trim();
  const isRowComplete = (i: ItemRow) => Boolean(i.work_date && i.description.trim() && i.hours.trim());

  const parsedItems = useMemo(
    () =>
      items
        .filter((i) => isRowComplete(i))
        .map((i) => ({ ...i, hoursNum: parseDecimal(i.hours) ?? NaN }))
        .sort((a, b) => a.work_date.localeCompare(b.work_date)),
    [items],
  );
  const incompleteItems = useMemo(
    () => items.filter((i) => !isRowEmpty(i) && !isRowComplete(i)),
    [items],
  );
  const totalHours = parsedItems.reduce((acc, i) => acc + (Number.isNaN(i.hoursNum) ? 0 : i.hoursNum), 0);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        lnwNumber: lnwNumber.trim(),
        issueDate,
        notes: notes.trim(),
        items: sortSnapshotItems(items).map((i) => [i.work_date, i.hours, i.description]),
      }),
    [lnwNumber, issueDate, notes, items],
  );
  const hasApprovedChanges = status === "approved" && initialSnapshot !== "" && currentSnapshot !== initialSnapshot;
  // Change detection for autosave: ignores empty rows and includes the selected order.
  const dirtySnapshot = useMemo(
    () =>
      JSON.stringify({
        orderId,
        lnwNumber: lnwNumber.trim(),
        issueDate,
        notes: notes.trim(),
        items: sortSnapshotItems(items.filter((i) => !isRowEmpty(i))).map((i) => [i.work_date, i.hours, i.description]),
      }),
    [orderId, lnwNumber, issueDate, notes, items],
  );

  const updateItem = (key: string, patch: Partial<ItemRow>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const removeItem = (key: string) => setItems((prev) => prev.filter((i) => i.key !== key));

  const generateItems = async () => {
    if (activityNotes.trim().length < 3) {
      toast.error("Bitte geben Sie zuerst Ihre Tätigkeitsnotizen ein.");
      return;
    }
    setAiBusy(true);
    setAiResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("lnw-ai-parse", {
        body: { notes: activityNotes.trim(), referenceDate: issueDate },
      });
      if (error) {
        const details = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
        let message = details;
        try {
          message = (JSON.parse(details) as { error?: string }).error ?? details;
        } catch {
          // Keep the returned safe message.
        }
        toast.error(message);
        return;
      }
      const result = data as AiResult;
      setAiResult(result);
      if (result.items.length === 0) toast.warning("Keine vollständigen Positionen erkannt. Bitte ergänzen Sie Datum und Stunden.");
    } catch (error) {
      console.error(error);
      toast.error("Die Notizen konnten nicht verarbeitet werden.");
    } finally {
      setAiBusy(false);
    }
  };

  const applyGeneratedItems = () => {
    if (!aiResult?.items.length) return;
    const generated = aiResult.items.map((item) => ({
      key: crypto.randomUUID(),
      work_date: item.work_date,
      hours: String(item.hours).replace(".", ","),
      description: item.description,
    }));
    setItems((previous) => {
      const emptyOnly = previous.length === 1 && !previous[0].hours && !previous[0].description.trim();
      return emptyOnly ? generated : [...previous, ...generated];
    });
    setActivityNotes("");
    setAiResult(null);
    toast.success(`${generated.length} ${generated.length === 1 ? "Position wurde" : "Positionen wurden"} übernommen.`);
  };

  const save = async (opts: { silent?: boolean; leaving?: boolean } = {}): Promise<string | null> => {
    const fail = (msg: string) => {
      if (!opts.silent) toast.error(msg);
      return null;
    };
    if (!orderId || !order) return fail("Bitte wählen Sie einen Auftrag.");
    if (!lnwNumber.trim()) return fail("Bitte vergeben Sie eine LNW-Kennung.");
    if (incompleteItems.length > 0) {
      return fail(
        incompleteItems.length === 1
          ? "Eine Position ist unvollständig. Bitte ergänzen Sie Datum, Stunden und Beschreibung oder löschen Sie die Zeile."
          : `${incompleteItems.length} Positionen sind unvollständig. Bitte ergänzen Sie Datum, Stunden und Beschreibung oder löschen Sie die Zeilen.`,
      );
    }
    if (parsedItems.some((i) => Number.isNaN(i.hoursNum) || i.hoursNum <= 0)) return fail("Bitte prüfen Sie die Stundenangaben.");
    const approvedChange = hasApprovedChanges;
    if (approvedChange && changeReason.trim().length < 5) {
      return fail("Bitte begründen Sie die Änderung an diesem genehmigten Leistungsnachweis.");
    }
    if (savingRef.current) return opts.silent ? null : savingRef.current;
    const snapAtStart = dirtySnapshot;
    const contentSnapAtStart = currentSnapshot;
    setSaving(true);
    const run = (async () => {
    try {
      let tsId = savedIdRef.current ?? (isNew ? "" : id ?? "");
      const payload = {
        order_id: orderId,
        project_id: order.project_id,
        lnw_number: lnwNumber.trim(),
        issue_date: issueDate,
        notes: notes.trim() || null,
        ...(approvedChange
          ? { change_reason: changeReason.trim(), change_reason_at: new Date().toISOString() }
          : {}),
      };
      if (!tsId) {
        const { data, error } = await supabase.from("timesheets").insert(payload).select("id").single();
        if (error) throw error;
        tsId = data.id;
        savedIdRef.current = tsId;
      } else {
        const { error } = await supabase.from("timesheets").update(payload).eq("id", tsId);
        if (error) throw error;
      }

      await supabase.from("timesheet_items").delete().eq("timesheet_id", tsId);
      if (parsedItems.length > 0) {
        const { error } = await supabase.from("timesheet_items").insert(
          parsedItems.map((i, idx) => ({
            timesheet_id: tsId,
            position: idx + 1,
            work_date: i.work_date,
            hours: i.hoursNum,
            description: i.description.trim(),
          })),
        );
        if (error) throw error;
      }

      if (!opts.silent) {
        setItems((prev) => {
          const complete = prev.filter((i) => isRowComplete(i)).sort((a, b) => a.work_date.localeCompare(b.work_date));
          const rest = prev.filter((i) => !isRowComplete(i));
          return [...complete, ...rest];
        });
        toast.success("Leistungsnachweis gespeichert");
      }
      setSavedSnapshot(snapAtStart);
      if (approvedChange) setInitialSnapshot(contentSnapAtStart);
      setLastSavedAt(new Date());
      setAutosaveError(false);
      if (isNew && !opts.leaving) navigate(`/admin/lnw/${tsId}`, { replace: true });
      return tsId;
    } catch (err) {
      console.error(err);
      setAutosaveError(true);
      toast.error("Speichern fehlgeschlagen. Eventuell ist die LNW-Kennung bereits vergeben.");
      return null;
    } finally {
      setSaving(false);
      savingRef.current = null;
    }
    })();
    savingRef.current = run;
    return run;
  };

  // ---- Autosave ----
  useEffect(() => { setAutosaveError(false); }, [dirtySnapshot]);
  const autosaveBlock = getAutosaveBlock({
    closed,
    invoiced: !!invoice.invoicedAt,
    hasOrder: !!order,
    hasLnwNumber: !!lnwNumber.trim(),
    incompleteCount: incompleteItems.length,
    invalidHours: parsedItems.some((i) => Number.isNaN(i.hoursNum) || i.hoursNum <= 0),
    approvedChanged: hasApprovedChanges,
    reasonLength: changeReason.trim().length,
  });
  const isDirty = savedSnapshot !== null && dirtySnapshot !== savedSnapshot && autosaveBlock !== "locked";

  useEffect(() => {
    if (!loading && savedSnapshot === null) setSavedSnapshot(dirtySnapshot);
  }, [loading, savedSnapshot, dirtySnapshot]);

  useEffect(() => {
    if (!isDirty || autosaveBlock || autosaveError) return;
    const t = window.setTimeout(() => { save({ silent: true }); }, 2000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtySnapshot, isDirty, autosaveBlock, autosaveError, saving]);

  const latest = useRef({ isDirty, autosaveBlock, save });
  latest.current = { isDirty, autosaveBlock, save };

  useEffect(() => {
    const flush = () => {
      const l = latest.current;
      if (l.isDirty && !l.autosaveBlock) l.save({ silent: true, leaving: true });
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!latest.current.isDirty) return;
      flush();
      e.preventDefault();
      e.returnValue = "";
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", onBeforeUnload);
      flush(); // leaving the editor inside the app
    };
  }, []);

  const leave = async () => {
    if (isDirty) {
      if (autosaveBlock) {
        const msg = autosaveBlock === "reason"
          ? "Änderungen nicht gespeichert, Begründung fehlt. Änderungen verwerfen und die Seite verlassen?"
          : "Änderungen nicht gespeichert, Angaben sind unvollständig. Änderungen verwerfen und die Seite verlassen?";
        if (!window.confirm(msg)) return;
        setSavedSnapshot(dirtySnapshot);
        latest.current.isDirty = false;
      } else {
        const ok = await save({ silent: true, leaving: true });
        if (!ok) return;
        latest.current.isDirty = false;
      }
    }
    navigate("/admin");
  };

  const toggleClosed = async () => {
    if (!id) return;
    if (closed) {
      const check = canReopen(order?.closed_at, "Auftrag");
      if (!check.ok) { toast.error(check.reason); return; }
    } else if (!window.confirm("Leistungsnachweis abschließen und archivieren?")) return;
    setClosing(true);
    const next = closed ? null : new Date().toISOString();
    const { error } = await supabase.from("timesheets").update({ closed_at: next }).eq("id", id);
    setClosing(false);
    if (error) { toast.error(dbErrorMessage(error, "Aktion fehlgeschlagen")); return; }
    setClosedAt(next);
    toast.success(next ? "Leistungsnachweis abgeschlossen" : "Leistungsnachweis wieder geöffnet");
  };

  const downloadPdf = async () => {
    const tsId = closed ? id ?? null : await save();
    if (!tsId || !order) return;
    setPdfBusy(true);
    try {
      await generateLnwPdf({
        lnwNumber: lnwNumber.trim(),
        issueDate,
        customerCompany: customer?.company ?? "",
        projectName: order.projects?.name ?? "",
        orderReference: order.order_number,
        status,
        approvedAt,
        approvedByName,
        changeReason: changeReason.trim() || savedChangeReason,
        items: parsedItems.map((i, idx) => ({
          position: idx + 1,
          work_date: i.work_date,
          hours: i.hoursNum,
          description: i.description.trim(),
        })),
      });
    } catch (err) {
      console.error(err);
      toast.error("PDF konnte nicht erzeugt werden");
    } finally {
      setPdfBusy(false);
    }
  };

  const getFunctionError = async (error: unknown) => {
    const details = error instanceof FunctionsHttpError ? await error.context.text() : error instanceof Error ? error.message : "Unbekannter Fehler";
    try {
      return (JSON.parse(details) as { error?: string }).error ?? details;
    } catch {
      return details;
    }
  };

  const generateApprovalLink = async () => {
    const tsId = await save();
    if (!tsId) return;
    setGeneratingLink(true);
    try {
      const { data, error } = await supabase.functions.invoke("lnw-send-invite", {
        body: { action: "generate", timesheetId: tsId, siteUrl: window.location.origin },
      });
      if (error) {
        toast.error("Link konnte nicht erzeugt werden: " + await getFunctionError(error));
        return;
      }
      setApprovalLink(data.link);
      setApprovalToken(data.token);
      setApprovalTimesheetId(tsId);
      setStatus("sent");
      setSentToEmail(null);
      setTokenExpiresAt(data.expiresAt ?? null);
      toast.success("Freigabelink wurde erstellt.");
    } finally {
      setGeneratingLink(false);
    }
  };

  const revokeApprovalLink = async () => {
    if (!id || isNew) return;
    setRevoking(true);
    try {
      const { error } = await supabase.functions.invoke("lnw-send-invite", {
        body: { action: "revoke", timesheetId: id, siteUrl: window.location.origin },
      });
      if (error) {
        toast.error("Link konnte nicht zurückgezogen werden: " + await getFunctionError(error));
        return;
      }
      setTokenExpiresAt(null);
      setApprovalLink("");
      setApprovalToken("");
      setSentToEmail(null);
      toast.success("Der Freigabelink wurde zurückgezogen und ist nicht mehr nutzbar.");
    } finally {
      setRevoking(false);
    }
  };


  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Freigabelink wurde kopiert.");
    } catch {
      toast.error("Der Link konnte nicht kopiert werden. Bitte markieren und kopieren Sie ihn manuell.");
    }
  };
  const copyApprovalLink = () => copyLink(approvalLink);

  useEffect(() => {
    if (isNew || !id || status !== "sent" || !hasActiveLink) {
      setActiveLink("");
      setActiveLinkUnavailable(false);
      return;
    }
    if (approvalLink) { setActiveLink(approvalLink); return; }
    let cancelled = false;
    supabase.functions
      .invoke("lnw-send-invite", { body: { action: "get_link", timesheetId: id, siteUrl: window.location.origin } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data?.link) { setActiveLink(data.link); setActiveLinkUnavailable(false); }
        else { setActiveLink(""); setActiveLinkUnavailable(true); }
      });
    return () => { cancelled = true; };
  }, [id, isNew, status, hasActiveLink, approvalLink, tokenExpiresAt]);

  const sendInvite = async () => {
    if (!approvalTimesheetId || !approvalToken) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("lnw-send-invite", {
        body: { action: "send", timesheetId: approvalTimesheetId, siteUrl: window.location.origin, token: approvalToken },
      });
      if (error) {
        toast.error("Versand fehlgeschlagen: " + await getFunctionError(error));
        return;
      }
      setSentToEmail(data.sentTo);
      toast.success(`Freigabelink wurde an ${data.sentTo} gesendet.`);
    } finally {
      setSending(false);
    }
  };

  const duplicate = async () => {
    const tsId = await save();
    if (!tsId || !id) return;
    const { data: copy, error } = await supabase
      .from("timesheets")
      .insert({
        order_id: orderId,
        project_id: order?.project_id ?? "",
        lnw_number: `${lnwNumber.trim()}-Kopie`,
        issue_date: format(new Date(), "yyyy-MM-dd"),
        notes: notes.trim() || null,
      })
      .select("id")
      .single();
    if (error || !copy) {
      toast.error("Duplizieren fehlgeschlagen");
      return;
    }
    if (parsedItems.length > 0) {
      await supabase.from("timesheet_items").insert(
        parsedItems.map((i, idx) => ({
          timesheet_id: copy.id,
          position: idx + 1,
          work_date: i.work_date,
          hours: i.hoursNum,
          description: i.description.trim(),
        })),
      );
    }
    toast.success("Kopie erstellt");
    navigate(`/admin/lnw/${copy.id}`);
  };

  if (loading) {
    return <p className="text-muted-foreground">Laden…</p>;
  }

  return (
    <>
      <Helmet>
        <title>{isNew ? "Neuer Leistungsnachweis" : `LNW ${lnwNumber}`} | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-2" onClick={leave}>
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isNew ? "Neuer Leistungsnachweis" : lnwNumber}
        </h1>
        <Badge variant={status === "approved" ? "default" : status === "rejected" ? "destructive" : status === "sent" ? "outline" : "secondary"}>
          {statusLabel[status]}
        </Badge>
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {saving
            ? "Speichert…"
            : autosaveError
              ? (
                <button type="button" className="text-destructive underline" onClick={() => save()}>
                  Speichern fehlgeschlagen, erneut versuchen
                </button>
              )
              : isDirty
                ? autosaveBlock === "reason"
                  ? "Ungespeicherte Änderungen: Begründung fehlt"
                  : "Ungespeicherte Änderungen"
                : lastSavedAt
                  ? `Gespeichert um ${format(lastSavedAt, "HH:mm")}`
                  : null}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" className="gap-2" onClick={duplicate}>
              <Copy className="h-4 w-4" /> Duplizieren
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-2" onClick={downloadPdf} disabled={pdfBusy || saving}>
            <FileDown className="h-4 w-4" /> {pdfBusy ? "PDF…" : "PDF herunterladen"}
          </Button>
          {!isNew && hasActiveLink && (
            <Button variant="outline" size="sm" className="gap-2" onClick={revokeApprovalLink} disabled={revoking}>
              <Ban className="h-4 w-4" /> {revoking ? "Wird zurückgezogen…" : "Link zurückziehen"}
            </Button>
          )}
          {!locked && (
            <AlertDialog open={shareDialogOpen} onOpenChange={(open) => {
              setShareDialogOpen(open);
              if (!open) {
                setApprovalLink("");
                setApprovalToken("");
                setApprovalTimesheetId("");
              }
            }}>
              <AlertDialogTrigger asChild>
                <Button size="sm" className="gap-2" disabled={sending || saving || parsedItems.length === 0}>
                  <Send className="h-4 w-4" /> Freigabe anfordern
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Freigabe anfordern</AlertDialogTitle>
                  <AlertDialogDescription>
                    Erstellen Sie zuerst einen persönlichen, 45 Tage gültigen Link. Anschließend können Sie ihn kopieren oder optional per E-Mail senden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {!approvalLink ? (
                  <AlertDialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch">
                    {hasActiveLink && (
                      <p className="text-sm text-destructive">
                        Achtung: Es existiert bereits ein gültiger Freigabelink
                        {sentToEmail ? ` (gesendet an ${sentToEmail})` : ""}
                        {tokenExpiresAt ? `, gültig bis ${format(new Date(tokenExpiresAt), "dd.MM.yyyy")}` : ""}.
                        Ein neuer Link macht den bisherigen sofort ungültig.
                      </p>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <Button onClick={generateApprovalLink} disabled={generatingLink || saving}>
                      {generatingLink ? "Link wird erstellt…" : hasActiveLink ? "Neuen Link erstellen" : "Freigabelink erstellen"}
                    </Button>
                    </div>
                  </AlertDialogFooter>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="approval-link">Freigabelink</Label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input id="approval-link" value={approvalLink} readOnly onFocus={(event) => event.currentTarget.select()} className="min-w-0" />
                        <Button type="button" variant="outline" className="shrink-0 gap-2" onClick={copyApprovalLink}>
                          <Copy className="h-4 w-4" /> Link kopieren
                        </Button>
                      </div>
                    </div>
                    {!recipientEmail && (
                      <p className="text-sm text-muted-foreground">Für diesen Auftrag ist keine E-Mail-Adresse hinterlegt. Sie können den Link dennoch direkt teilen.</p>
                    )}
                    <AlertDialogFooter>
                      <AlertDialogCancel>Schließen</AlertDialogCancel>
                      <Button onClick={sendInvite} disabled={sending || !recipientEmail} className="gap-2">
                        <Send className="h-4 w-4" /> {sending ? "Wird gesendet…" : `Per E-Mail senden${recipientEmail ? ` (${recipientEmail})` : ""}`}
                      </Button>
                    </AlertDialogFooter>
                  </div>
                )}
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {status === "approved" && approvedAt && (
        <Card className="mb-6 border-primary/40 bg-primary/5">
          <CardContent className="py-4 text-sm">
            Genehmigt am {format(new Date(approvedAt), "dd.MM.yyyy 'um' HH:mm")} durch {approvedByName ?? "Auftraggeber"}.
            Der Leistungsnachweis ist gesperrt und kann als PDF heruntergeladen werden.
          </CardContent>
        </Card>
      )}
      {!isNew && status === "approved" && (
        <Card className="mb-6 border-secondary/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Abrechnung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {!invoice.id ? (
              <>
                <div className="grid gap-2 sm:grid-cols-4">
                  <div><div className="text-muted-foreground">Kunde</div>{customer?.company ?? "–"}</div>
                  <div><div className="text-muted-foreground">Stunden</div>{fmtHours(totalHours)} h</div>
                  <div><div className="text-muted-foreground">Stundensatz</div>{order?.hourly_rate ? fmtEur(Number(order.hourly_rate)) : "fehlt"}</div>
                  <div><div className="text-muted-foreground">Netto</div><strong>{fmtEur(totalHours * Number(order?.hourly_rate ?? 0))}</strong></div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="invoice-text">Positionstext</Label>
                  <Input id="invoice-text" value={invoiceText} onChange={(e) => setInvoiceText(e.target.value)} maxLength={500} />
                </div>
                <Button onClick={createInvoice} disabled={invoiceBusy || closed} className="gap-2">
                  {invoiceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Rechnungsentwurf in Lexware anlegen
                </Button>
              </>
            ) : (
              <>
                <p>
                  {invoice.invoicedAt
                    ? <>Abgerechnet{invoice.number ? <> mit Rechnung <strong>{invoice.number}</strong></> : ""} am {new Date(invoice.invoicedAt).toLocaleDateString("de-DE")}. Positionen sind gesperrt.</>
                    : <>Rechnungsentwurf liegt in Lexware. Öffnen, LNW-PDF anhängen und aus Lexware versenden.</>}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a href={`https://app.lexware.de/permalink/invoices/${invoice.invoicedAt ? "view" : "edit"}/${invoice.id}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> In Lexware öffnen</a>
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={downloadPdf} disabled={pdfBusy}><FileDown className="h-4 w-4" /> LNW-PDF</Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => refreshInvoice(false)} disabled={invoiceBusy}><RefreshCw className="h-4 w-4" /> Status prüfen</Button>
                  {!invoice.invoicedAt && (
                    <Button variant="outline" size="sm" className="gap-2" onClick={markInvoiced} disabled={invoiceBusy}><Check className="h-4 w-4" /> Als abgerechnet markieren</Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
      {status === "rejected" && (
        <Card className="mb-6 border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm">
            Abgelehnt{rejectionComment ? `: ${rejectionComment}` : ""}. Sie können den Nachweis anpassen und erneut senden.
          </CardContent>
        </Card>
      )}
      {!isNew && (closed || canCloseTimesheet(status).ok) && (
        <Card className={`mb-6 ${closed ? "border-dashed bg-muted/40" : ""}`}>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Archive className="h-4 w-4" />
              {closed
                ? `Abgeschlossen am ${new Date(closedAt!).toLocaleDateString("de-DE")}, archiviert und schreibgeschützt.`
                : "Dieser Leistungsnachweis kann abgeschlossen und archiviert werden."}
            </span>
            <Button variant="outline" size="sm" className="gap-2" onClick={toggleClosed} disabled={closing}>
              {closed ? <><ArchiveRestore className="h-4 w-4" /> Wieder öffnen</> : <><Archive className="h-4 w-4" /> Abschließen</>}
            </Button>
          </CardContent>
        </Card>
      )}
      {status === "sent" && (
        <Card className="mb-6">
          <CardContent className="space-y-3 py-4 text-sm text-muted-foreground">
            <p>
              {sentToEmail
                ? <>Freigabelink wurde an {sentToEmail} gesendet. Warten auf Rückmeldung des Kunden.</>
                : <>Ein neuer Freigabelink wurde erstellt, aber noch nicht per E-Mail versendet. Kopieren Sie den Link oder senden Sie ihn über „Freigabe anfordern“.</>}
            </p>
            {hasActiveLink && (activeLink ? (
              <div className="space-y-2">
                <Label htmlFor="active-approval-link">
                  Aktiver Freigabelink{tokenExpiresAt ? `, gültig bis ${format(new Date(tokenExpiresAt), "dd.MM.yyyy")}` : ""}
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input id="active-approval-link" value={activeLink} readOnly onFocus={(e) => e.currentTarget.select()} className="min-w-0" />
                  <Button type="button" variant="outline" className="shrink-0 gap-2" onClick={() => copyLink(activeLink)}>
                    <Copy className="h-4 w-4" /> Link kopieren
                  </Button>
                </div>
              </div>
            ) : activeLinkUnavailable ? (
              <p className="text-xs">Dieser Link wurde vor Einführung der Kopierfunktion erstellt und kann nicht erneut angezeigt werden. Ab dem nächsten erstellten Link ist er hier jederzeit kopierbar.</p>
            ) : null)}
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Kopfdaten</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Kunde *</Label>
            <Select value={customerId} onValueChange={handleCustomerChange} disabled={locked}>
              <SelectTrigger><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>
                {customerOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Projekt *</Label>
            <Select value={projectId} onValueChange={handleProjectChange} disabled={locked || !customerId}>
              <SelectTrigger><SelectValue placeholder={customerId ? "Projekt wählen" : "Zuerst Kunde wählen"} /></SelectTrigger>
              <SelectContent>
                {projectOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Auftrag *</Label>
            <Select value={orderId} onValueChange={setOrderId} disabled={locked || !projectId}>
              <SelectTrigger><SelectValue placeholder={projectId ? "Auftrag wählen" : "Zuerst Projekt wählen"} /></SelectTrigger>
              <SelectContent>
                {orderOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {order && (
              <p className="text-xs text-muted-foreground">
                Auftragskennung: {order.order_number ?? "–"}
                {order.hourly_rate != null ? ` · ${fmtHours(Number(order.hourly_rate))} € / h` : ""}
                {order.budget_hours != null
                  ? ` · Kontingent ${fmtHours(Number(order.budget_hours))} h, davon genehmigt ${fmtHours(orderTotals[order.id]?.approved ?? 0)} h, verbleibend ${fmtHours(Number(order.budget_hours) - (orderTotals[order.id]?.approved ?? 0) - Number(order.external_hours ?? 0))} h`
                  : ""}
                {order.contact?.name ? ` · Ansprechpartner: ${order.contact.name}` : ""}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lnw-number">LNW-Kennung *</Label>
            <Input id="lnw-number" value={lnwNumber} onChange={(e) => setLnwNumber(e.target.value)} disabled={locked} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="issue-date">Datum *</Label>
            <Input id="issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} disabled={locked} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Bemerkung (optional, erscheint nicht im PDF)</Label>
            <Textarea id="notes" rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={locked} />
          </div>
        </CardContent>
      </Card>

      {!locked && (
        <Card className="mb-6 border-secondary/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-secondary" /> Tätigkeitsnotizen strukturieren
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="activity-notes">Freie Notizen</Label>
              <Textarea
                id="activity-notes"
                rows={6}
                maxLength={12000}
                value={activityNotes}
                onChange={(event) => {
                  setActivityNotes(event.target.value);
                  if (aiResult) setAiResult(null);
                }}
                placeholder={"10.09.2026, 3 Std. Workshop zur OT-Netzwerkarchitektur\n11.09.2026, 1,5 Std. Dokumentation und Abstimmung"}
                disabled={aiBusy}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">Datum und Stunden müssen aus den Notizen eindeutig hervorgehen.</p>
                <Button type="button" variant="secondary" className="gap-2" onClick={generateItems} disabled={aiBusy || activityNotes.trim().length < 3}>
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {aiBusy ? "Positionen werden erzeugt…" : "Positionen erzeugen"}
                </Button>
              </div>
            </div>

            {aiResult && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">Vorschau: {aiResult.items.length} {aiResult.items.length === 1 ? "Position" : "Positionen"}</p>
                  <Button type="button" size="sm" className="gap-2" onClick={applyGeneratedItems} disabled={aiResult.items.length === 0}>
                    <Check className="h-4 w-4" /> Positionen übernehmen
                  </Button>
                </div>
                {aiResult.items.map((item, index) => (
                  <div key={`${item.work_date}-${index}`} className="grid grid-cols-1 gap-2 rounded-md border bg-muted/30 p-3 text-sm md:grid-cols-[120px_80px_1fr]">
                    <span>{format(new Date(`${item.work_date}T00:00:00`), "dd.MM.yyyy")}</span>
                    <span>{fmtHours(item.hours)} h</span>
                    <span>{item.description}</span>
                  </div>
                ))}
                {aiResult.warnings.length > 0 && (
                  <div className="rounded-md border border-cta/30 bg-cta/5 p-3 text-sm">
                    <p className="mb-1 font-medium">Bitte prüfen</p>
                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                      {aiResult.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <fieldset disabled={closed} className="contents">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Positionen</CardTitle>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setItems((prev) => [...prev, newItem()])}>
            <Plus className="h-4 w-4" /> Position
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {status === "approved" && (
            <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <p>
                Dieser Leistungsnachweis ist bereits genehmigt. Änderungen an Positionen verändern das genehmigte Dokument; die genehmigten Stunden des Auftrags werden automatisch neu berechnet.
              </p>
              {hasApprovedChanges && (
                <div className="space-y-2">
                  <Label htmlFor="change-reason">Begründung der Änderung *</Label>
                  <Textarea
                    id="change-reason"
                    rows={2}
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="z. B. Korrektur der Stunden nach Rücksprache mit dem Auftraggeber"
                  />
                  <p className="text-xs">Die Begründung wird gespeichert und im PDF vermerkt.</p>
                </div>
              )}
            </div>
          )}
          <div className="hidden grid-cols-[130px_90px_1fr_40px] gap-3 text-sm font-medium text-muted-foreground md:grid">
            <span>Datum</span><span>Stunden</span><span>Beschreibung</span><span />
          </div>
          {items.map((item, idx) => (
            <div key={item.key} className="grid grid-cols-1 gap-2 rounded-lg border p-3 md:grid-cols-[130px_90px_1fr_40px] md:items-center md:gap-3 md:border-0 md:p-0">
              <div>
                <Label className="md:hidden">Datum</Label>
                <Input type="date" value={item.work_date} onChange={(e) => updateItem(item.key, { work_date: e.target.value })} />
              </div>
              <div>
                <Label className="md:hidden">Stunden</Label>
                <Input inputMode="decimal" placeholder="Std." value={item.hours} onChange={(e) => updateItem(item.key, { hours: e.target.value })} />
              </div>
              <div>
                <Label className="md:hidden">Beschreibung</Label>
                <Input placeholder={`Position ${idx + 1}`} value={item.description} onChange={(e) => updateItem(item.key, { description: e.target.value })} />
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeItem(item.key)} aria-label="Position entfernen">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex items-center justify-between border-t pt-4 text-lg font-semibold">
            <span>Gesamtaufwand</span>
            <span>{fmtHours(totalHours)} h</span>
          </div>
        </CardContent>
      </Card>
      </fieldset>

      <div className="mt-6 flex justify-end">
        <Button onClick={() => save()} disabled={saving || closed} className="gap-2">
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </>
  );
};

export default AdminTimesheetEdit;
